import { ColumnDef, SortingState } from "@tanstack/react-table";
import { Tooltip } from "antd";
import { useMemo, useState } from "react";

import {
  ISnomedEdition,
  ISnomedMedication,
  ISnomedMedicationSemanticTag,
  ISnomedMedicationSortBy,
} from "../../../@types/admin/AdminMedication";
import { TableSorted } from "../../../components/TableSorted";
import {
  SNOMED_MEDICATIONS_QUERY_KEY,
  useSnomedMedicationSummary,
  useSnomedMedications,
} from "../../../hooks/queries/admin/snomedMedications";
import { useDebounce } from "../../../hooks/useDebounce";
import { getLongFormattedDate } from "../../../utils/dateFormatter";
import {
  AdminSelectField,
  ErrorMessage,
  FilterGroup,
  FilterLabel,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageInfoTooltip,
  PageTitle,
  PageTitleGroup,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  TruncatedCell,
} from "../shared";
import {
  EditionChip,
  EditionCode,
  EditionTooltip,
  EditionTooltipTitle,
  EditionVersion,
  SummaryRow,
} from "./AdminMedications.styles";

const DEFAULT_PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;

/** Edition codes in the order the summary cards and the filter list them. */
export const EDITION_LABELS: Record<ISnomedEdition, string> = {
  INT: "International",
  IE: "Ireland",
  BE: "Belgium",
};

/** API enum name → label; the row itself carries the lowercase stored tag. */
export const SEMANTIC_TAG_LABELS: Record<ISnomedMedicationSemanticTag, string> =
  {
    MedicinalProduct: "Medicinal product",
    RealMedicinalProduct: "Real medicinal product",
    ClinicalDrug: "Clinical drug",
    RealClinicalDrug: "Real clinical drug",
  };

type IStatusFilter = "" | "active" | "retired";

/** Column id → API sort key. Columns missing here are not sortable. */
const SORT_KEYS: Record<string, ISnomedMedicationSortBy> = {
  conceptId: "ConceptId",
  preferredTerm: "PreferredTerm",
  edition: "Edition",
  semanticTag: "SemanticTag",
  status: "Active",
  updated: "Updated",
};

const DEFAULT_SORTING: SortingState = [{ id: "preferredTerm", desc: false }];

function editionLabel(edition: string): string {
  return EDITION_LABELS[edition as ISnomedEdition] ?? edition;
}

function capitalise(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * Medications admin page (EN-5209): read-only browser over the SNOMED CT
 * medication reference data (`public."SnomedMedications"`) — the catalogue
 * the app's "Set up medication" search reads. One card per seeded edition
 * says how much each release contributed; the table below is filtered by
 * search, edition, semantic tag and status, and paged and sorted on the
 * server because the catalogue is ~30k rows.
 *
 * A patient's own medications are the Treatments page, not this one.
 */
export function AdminMedications() {
  document.title = "SNOMED medications - Admin";

  const [searchTerm, setSearchTerm] = useState("");
  const [edition, setEdition] = useState<ISnomedEdition | "">("");
  const [semanticTag, setSemanticTag] = useState<
    ISnomedMedicationSemanticTag | ""
  >("");
  const [status, setStatus] = useState<IStatusFilter>("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);

  // Typing fires nothing until the operator pauses — the search itself is
  // server-side, and the SNOMED search field in the app debounces the same way.
  const debouncedSearch = useDebounce(searchTerm, SEARCH_DEBOUNCE_MS);

  const sort = sorting[0] ?? DEFAULT_SORTING[0];
  const sortBy = SORT_KEYS[sort.id] ?? "PreferredTerm";

  const { medications, totalCount, isFetching, isError } = useSnomedMedications(
    {
      page,
      pageSize,
      search: debouncedSearch.trim(),
      edition,
      semanticTag,
      active: status === "" ? undefined : status === "active",
      sortBy,
      sortDescending: sort.desc,
    }
  );
  const { summary, isError: isSummaryError } = useSnomedMedicationSummary();

  const resetToFirstPage = () => setPage(1);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    resetToFirstPage();
  };

  const handleSortingChange = (next: SortingState) => {
    // Only server-sortable columns are sortable in the table, so an empty
    // state (third click) falls back to the default rather than "unsorted".
    setSorting(next.length > 0 ? next : DEFAULT_SORTING);
    resetToFirstPage();
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pagination = {
    currentPage: page,
    pageSize,
    totalPages,
    totalItems: totalCount,
    hasNextPage: page < totalPages,
    hasPreviousPage: page > 1,
  };

  const columns: ColumnDef<ISnomedMedication, string>[] = useMemo(
    () => [
      {
        accessorKey: "conceptId",
        header: "Concept ID",
        cell: ({ row }) => <span>{row.original.conceptId}</span>,
      },
      {
        accessorKey: "preferredTerm",
        header: "Preferred term",
      },
      {
        accessorKey: "fullySpecifiedName",
        header: "Fully specified name",
        cell: ({ row }) => (
          <TruncatedCell title={row.original.fullySpecifiedName}>
            {row.original.fullySpecifiedName}
          </TruncatedCell>
        ),
        enableSorting: false,
      },
      {
        id: "edition",
        header: "Edition",
        accessorFn: (medication) =>
          `${medication.edition} · ${editionLabel(medication.edition)}`,
      },
      {
        id: "semanticTag",
        header: "Semantic tag",
        accessorFn: (medication) =>
          medication.semanticTag ? capitalise(medication.semanticTag) : "",
      },
      {
        // `active` is a boolean, and TableSorted dashes a falsy value — the
        // accessor renders the word so "Retired" is explicit. Headed "Status"
        // because a column headed "Active" reads as a filter.
        id: "status",
        header: "Status",
        accessorFn: (medication) => (medication.active ? "Active" : "Retired"),
        cell: ({ row }) => (
          <StatusBadge status={row.original.active ? "active" : "inactive"}>
            {row.original.active ? "Active" : "Retired"}
          </StatusBadge>
        ),
      },
      {
        id: "updated",
        header: "Last seeded",
        accessorFn: (medication) =>
          getLongFormattedDate(medication.updated ?? medication.created, "-"),
      },
    ],
    []
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitleGroup>
          <PageTitle>SNOMED medications</PageTitle>
          <PageInfoTooltip title="SNOMED medications">
            SNOMED CT medication reference data the app&apos;s medication search
            reads — not a patient&apos;s own medications (those are Treatments).
            National editions are seeded as extension-only rows on top of the
            International base; a patient searches their hospital&apos;s edition
            plus International only. Hover an edition for its active / retired
            split and rows per semantic tag.
          </PageInfoTooltip>
        </PageTitleGroup>

        {/* One chip per seeded edition; the detail lives in the tooltip so the
            header stays a single line (EN-5209 UI feedback). */}
        {summary.length > 0 && (
          <SummaryRow data-testid="medications-summary">
            {summary.map((item) => (
              <Tooltip
                key={item.edition}
                placement="bottom"
                title={
                  <EditionTooltip>
                    <EditionTooltipTitle>
                      {item.edition} · {editionLabel(item.edition)}
                    </EditionTooltipTitle>
                    {item.versionLabel && (
                      <span>
                        Release: {item.versionLabel} ({item.version})
                      </span>
                    )}
                    {item.packageName && <span>{item.packageName}</span>}
                    <span>
                      {item.active.toLocaleString("en-GB")} active ·{" "}
                      {item.inactive.toLocaleString("en-GB")} retired
                    </span>
                    {Object.entries(item.activeBySemanticTag).map(
                      ([tag, count]) => (
                        <span key={tag}>
                          {capitalise(tag)}: {count.toLocaleString("en-GB")}
                        </span>
                      )
                    )}
                  </EditionTooltip>
                }
              >
                <EditionChip
                  tabIndex={0}
                  aria-label={`${editionLabel(
                    item.edition
                  )}: ${item.total.toLocaleString("en-GB")} medications`}
                >
                  <EditionCode>{item.edition}</EditionCode>
                  {item.total.toLocaleString("en-GB")}
                  {item.versionLabel && (
                    <EditionVersion>· {item.versionLabel}</EditionVersion>
                  )}
                </EditionChip>
              </Tooltip>
            ))}
          </SummaryRow>
        )}
      </PageHeader>

      {isSummaryError && (
        <ErrorMessage>Failed to load the per-edition summary.</ErrorMessage>
      )}

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by preferred term or SNOMED concept id..."
          value={searchTerm}
          onChange={(e) => handleSearchChange(e.target.value)}
        />

        <FilterGroup>
          <FilterLabel htmlFor="medications-edition">Edition</FilterLabel>
          <AdminSelectField
            id="medications-edition"
            value={edition}
            onChange={(e) => {
              setEdition(e.target.value as ISnomedEdition | "");
              resetToFirstPage();
            }}
          >
            <option value="">All editions</option>
            {(Object.keys(EDITION_LABELS) as ISnomedEdition[]).map((code) => (
              <option key={code} value={code}>
                {code} · {EDITION_LABELS[code]}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="medications-semantic-tag">
            Semantic tag
          </FilterLabel>
          <AdminSelectField
            id="medications-semantic-tag"
            value={semanticTag}
            onChange={(e) => {
              setSemanticTag(
                e.target.value as ISnomedMedicationSemanticTag | ""
              );
              resetToFirstPage();
            }}
          >
            <option value="">All semantic tags</option>
            {(
              Object.keys(SEMANTIC_TAG_LABELS) as ISnomedMedicationSemanticTag[]
            ).map((tag) => (
              <option key={tag} value={tag}>
                {SEMANTIC_TAG_LABELS[tag]}
              </option>
            ))}
          </AdminSelectField>
        </FilterGroup>

        <FilterGroup>
          <FilterLabel htmlFor="medications-status">Status</FilterLabel>
          <AdminSelectField
            id="medications-status"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as IStatusFilter);
              resetToFirstPage();
            }}
          >
            <option value="">Active and retired</option>
            <option value="active">Active</option>
            <option value="retired">Retired</option>
          </AdminSelectField>
        </FilterGroup>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the medications. Adjust the filters and try again.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[[SNOMED_MEDICATIONS_QUERY_KEY]]}>
          <TableSorted
            columns={columns}
            data={medications}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            manualSorting
            emptyStateText={
              isFetching ? (
                <TableLoadingState />
              ) : (
                "No medications match these filters"
              )
            }
          />
        </TableWrapper>
        <TablePagination
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            resetToFirstPage();
          }}
          pageSizeOptions={[25, 50, 100, 250]}
          // Server-paginated: the export can only write the page that has
          // been fetched. The button's row count says so.
          exportCsv={{
            filename: "snomed-medications",
            columns,
            rows: medications,
            currentPageOnly: true,
          }}
        />
      </TableContainer>
    </PageContainer>
  );
}
